import React, { createContext, useContext, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// All 20 supported languages
export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳' },
  { code: 'mr', name: 'Marathi', nativeName: 'मराठी', flag: '🇮🇳' },
  { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்', flag: '🇮🇳' },
  { code: 'te', name: 'Telugu', nativeName: 'తెలుగు', flag: '🇮🇳' },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা', flag: '🇮🇳' },
  { code: 'gu', name: 'Gujarati', nativeName: 'ગુજરાતી', flag: '🇮🇳' },
  { code: 'kn', name: 'Kannada', nativeName: 'ಕನ್ನಡ', flag: '🇮🇳' },
  { code: 'ml', name: 'Malayalam', nativeName: 'മലയാളം', flag: '🇮🇳' },
  { code: 'pa', name: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ', flag: '🇮🇳' },
  { code: 'or', name: 'Odia', nativeName: 'ଓଡ଼ିଆ', flag: '🇮🇳' },
  { code: 'as', name: 'Assamese', nativeName: 'অসমীয়া', flag: '🇮🇳' },
  { code: 'ur', name: 'Urdu', nativeName: 'اردو', flag: '🇵🇰', rtl: true },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦', rtl: true },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵' },
];

// UI Labels in all languages
export const UI_LABELS = {
  en: {
    // Navigation
    home: 'Home',
    dashboard: 'Dashboard',
    assessment: 'Assessment',
    reports: 'Reports',
    profile: 'Profile',
    logout: 'Logout',
    login: 'Login',
    signIn: 'Sign In',
    signUp: 'Sign Up',
    getStarted: 'Get Started',
    
    // Assessment
    startAssessment: 'Start Assessment',
    continueAssessment: 'Continue Assessment',
    submitAssessment: 'Submit Assessment',
    nextQuestion: 'Next',
    previousQuestion: 'Previous',
    questionOf: 'Question {current} of {total}',
    timeRemaining: 'Time Remaining',
    assessmentComplete: 'Assessment Complete',
    
    // Reports
    viewReport: 'View Report',
    downloadReport: 'Download Report',
    reportReady: 'Your Report is Ready',
    reportPending: 'Report Processing',
    reportWillBeEmailed: 'Your report will be emailed within 24-48 hours',
    
    // Auth
    emailAddress: 'Email Address',
    password: 'Password',
    fullName: 'Full Name',
    mobileNumber: 'Mobile Number',
    dateOfBirth: 'Date of Birth',
    timeOfBirth: 'Time of Birth',
    placeOfBirth: 'Place of Birth',
    createAccount: 'Create Account',
    alreadyHaveAccount: 'Already have an account?',
    dontHaveAccount: "Don't have an account?",
    
    // Common
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    cancel: 'Cancel',
    save: 'Save',
    submit: 'Submit',
    back: 'Back',
    next: 'Next',
    yes: 'Yes',
    no: 'No',
    
    // Landing
    heroTitle: 'Decode Your Leadership DNA',
    heroSubtitle: 'An unprecedented and one-of-its-kind assessment where 22 proprietary evaluation frameworks are synthesized with 5000+ years of ancient Indian wisdom sciences.',
    beginAssessment: 'Begin Your Assessment',
    
    // Aryan
    chatWithAryan: 'Chat with Aryan',
    askAryan: 'Ask Aryan anything about your report...',
    aryanGreeting: 'Hello! I am Aryan, your personal mentor. How can I help you today?',
  },
  hi: {
    // Navigation
    home: 'होम',
    dashboard: 'डैशबोर्ड',
    assessment: 'मूल्यांकन',
    reports: 'रिपोर्ट्स',
    profile: 'प्रोफाइल',
    logout: 'लॉगआउट',
    login: 'लॉगिन',
    signIn: 'साइन इन',
    signUp: 'साइन अप',
    getStarted: 'शुरू करें',
    
    // Assessment
    startAssessment: 'मूल्यांकन शुरू करें',
    continueAssessment: 'मूल्यांकन जारी रखें',
    submitAssessment: 'मूल्यांकन जमा करें',
    nextQuestion: 'अगला',
    previousQuestion: 'पिछला',
    questionOf: 'प्रश्न {current} / {total}',
    timeRemaining: 'शेष समय',
    assessmentComplete: 'मूल्यांकन पूर्ण',
    
    // Reports
    viewReport: 'रिपोर्ट देखें',
    downloadReport: 'रिपोर्ट डाउनलोड करें',
    reportReady: 'आपकी रिपोर्ट तैयार है',
    reportPending: 'रिपोर्ट प्रोसेसिंग',
    reportWillBeEmailed: 'आपकी रिपोर्ट 24-48 घंटों में ईमेल की जाएगी',
    
    // Auth
    emailAddress: 'ईमेल पता',
    password: 'पासवर्ड',
    fullName: 'पूरा नाम',
    mobileNumber: 'मोबाइल नंबर',
    dateOfBirth: 'जन्म तिथि',
    timeOfBirth: 'जन्म समय',
    placeOfBirth: 'जन्म स्थान',
    createAccount: 'खाता बनाएं',
    alreadyHaveAccount: 'पहले से खाता है?',
    dontHaveAccount: 'खाता नहीं है?',
    
    // Common
    loading: 'लोड हो रहा है...',
    error: 'त्रुटि',
    success: 'सफलता',
    cancel: 'रद्द करें',
    save: 'सहेजें',
    submit: 'जमा करें',
    back: 'वापस',
    next: 'अगला',
    yes: 'हाँ',
    no: 'नहीं',
    
    // Landing
    heroTitle: 'अपने नेतृत्व DNA को समझें',
    heroSubtitle: 'एक अभूतपूर्व मूल्यांकन जहां 22 मूल्यांकन फ्रेमवर्क को 5000+ वर्षों की प्राचीन भारतीय ज्ञान विज्ञान के साथ संश्लेषित किया गया है।',
    beginAssessment: 'मूल्यांकन शुरू करें',
    
    // Aryan
    chatWithAryan: 'अर्यन से बात करें',
    askAryan: 'अपनी रिपोर्ट के बारे में अर्यन से कुछ भी पूछें...',
    aryanGreeting: 'नमस्ते! मैं अर्यन हूं, आपका व्यक्तिगत मार्गदर्शक। आज मैं आपकी कैसे मदद कर सकता हूं?',
  },
  mr: {
    // Navigation
    home: 'होम',
    dashboard: 'डॅशबोर्ड',
    assessment: 'मूल्यांकन',
    reports: 'अहवाल',
    profile: 'प्रोफाइल',
    logout: 'लॉगआउट',
    login: 'लॉगिन',
    signIn: 'साइन इन',
    signUp: 'साइन अप',
    getStarted: 'सुरू करा',
    
    // Assessment
    startAssessment: 'मूल्यांकन सुरू करा',
    continueAssessment: 'मूल्यांकन सुरू ठेवा',
    submitAssessment: 'मूल्यांकन सबमिट करा',
    nextQuestion: 'पुढे',
    previousQuestion: 'मागे',
    questionOf: 'प्रश्न {current} / {total}',
    timeRemaining: 'उर्वरित वेळ',
    assessmentComplete: 'मूल्यांकन पूर्ण',
    
    // Reports
    viewReport: 'अहवाल पहा',
    downloadReport: 'अहवाल डाउनलोड करा',
    reportReady: 'तुमचा अहवाल तयार आहे',
    reportPending: 'अहवाल प्रक्रियेत',
    reportWillBeEmailed: 'तुमचा अहवाल 24-48 तासांत ईमेल केला जाईल',
    
    // Auth
    emailAddress: 'ईमेल पत्ता',
    password: 'पासवर्ड',
    fullName: 'पूर्ण नाव',
    mobileNumber: 'मोबाइल नंबर',
    dateOfBirth: 'जन्मतारीख',
    timeOfBirth: 'जन्मवेळ',
    placeOfBirth: 'जन्मस्थान',
    createAccount: 'खाते तयार करा',
    alreadyHaveAccount: 'आधीच खाते आहे?',
    dontHaveAccount: 'खाते नाही?',
    
    // Common
    loading: 'लोड होत आहे...',
    error: 'त्रुटी',
    success: 'यशस्वी',
    cancel: 'रद्द करा',
    save: 'जतन करा',
    submit: 'सबमिट करा',
    back: 'मागे',
    next: 'पुढे',
    yes: 'होय',
    no: 'नाही',
    
    // Landing
    heroTitle: 'तुमचे नेतृत्व DNA उलगडा',
    heroSubtitle: 'एक अभूतपूर्व मूल्यांकन जिथे 22 मूल्यांकन फ्रेमवर्क 5000+ वर्षांच्या प्राचीन भारतीय ज्ञान विज्ञानासह संश्लेषित केले आहेत।',
    beginAssessment: 'मूल्यांकन सुरू करा',
    
    // Aryan
    chatWithAryan: 'अर्यनशी बोला',
    askAryan: 'तुमच्या अहवालाबद्दल अर्यनला काहीही विचारा...',
    aryanGreeting: 'नमस्कार! मी अर्यन आहे, तुमचा वैयक्तिक मार्गदर्शक. आज मी तुमची कशी मदत करू शकतो?',
  },
  ar: {
    // Navigation
    home: 'الرئيسية',
    dashboard: 'لوحة التحكم',
    assessment: 'التقييم',
    reports: 'التقارير',
    profile: 'الملف الشخصي',
    logout: 'تسجيل الخروج',
    login: 'تسجيل الدخول',
    signIn: 'تسجيل الدخول',
    signUp: 'إنشاء حساب',
    getStarted: 'ابدأ الآن',
    
    // Assessment
    startAssessment: 'بدء التقييم',
    continueAssessment: 'متابعة التقييم',
    submitAssessment: 'إرسال التقييم',
    nextQuestion: 'التالي',
    previousQuestion: 'السابق',
    questionOf: 'السؤال {current} من {total}',
    timeRemaining: 'الوقت المتبقي',
    assessmentComplete: 'اكتمل التقييم',
    
    // Reports
    viewReport: 'عرض التقرير',
    downloadReport: 'تحميل التقرير',
    reportReady: 'تقريرك جاهز',
    reportPending: 'جاري معالجة التقرير',
    reportWillBeEmailed: 'سيتم إرسال تقريرك عبر البريد الإلكتروني خلال 24-48 ساعة',
    
    // Auth
    emailAddress: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    fullName: 'الاسم الكامل',
    mobileNumber: 'رقم الهاتف',
    dateOfBirth: 'تاريخ الميلاد',
    timeOfBirth: 'وقت الميلاد',
    placeOfBirth: 'مكان الميلاد',
    createAccount: 'إنشاء حساب',
    alreadyHaveAccount: 'لديك حساب بالفعل؟',
    dontHaveAccount: 'ليس لديك حساب؟',
    
    // Common
    loading: 'جاري التحميل...',
    error: 'خطأ',
    success: 'نجاح',
    cancel: 'إلغاء',
    save: 'حفظ',
    submit: 'إرسال',
    back: 'رجوع',
    next: 'التالي',
    yes: 'نعم',
    no: 'لا',
    
    // Landing
    heroTitle: 'اكتشف حمضك النووي القيادي',
    heroSubtitle: 'تقييم فريد من نوعه يجمع بين 22 إطار تقييم و5000+ عام من علوم الحكمة الهندية القديمة.',
    beginAssessment: 'ابدأ التقييم',
    
    // Aryan
    chatWithAryan: 'تحدث مع أريان',
    askAryan: 'اسأل أريان أي شيء عن تقريرك...',
    aryanGreeting: 'مرحباً! أنا أريان، مرشدك الشخصي. كيف يمكنني مساعدتك اليوم؟',
  },
  fr: {
    // Navigation
    home: 'Accueil',
    dashboard: 'Tableau de bord',
    assessment: 'Évaluation',
    reports: 'Rapports',
    profile: 'Profil',
    logout: 'Déconnexion',
    login: 'Connexion',
    signIn: 'Se connecter',
    signUp: "S'inscrire",
    getStarted: 'Commencer',
    
    // Assessment
    startAssessment: "Démarrer l'évaluation",
    continueAssessment: "Continuer l'évaluation",
    submitAssessment: "Soumettre l'évaluation",
    nextQuestion: 'Suivant',
    previousQuestion: 'Précédent',
    questionOf: 'Question {current} sur {total}',
    timeRemaining: 'Temps restant',
    assessmentComplete: 'Évaluation terminée',
    
    // Reports
    viewReport: 'Voir le rapport',
    downloadReport: 'Télécharger le rapport',
    reportReady: 'Votre rapport est prêt',
    reportPending: 'Rapport en cours de traitement',
    reportWillBeEmailed: 'Votre rapport vous sera envoyé par email sous 24-48 heures',
    
    // Auth
    emailAddress: 'Adresse email',
    password: 'Mot de passe',
    fullName: 'Nom complet',
    mobileNumber: 'Numéro de téléphone',
    dateOfBirth: 'Date de naissance',
    timeOfBirth: 'Heure de naissance',
    placeOfBirth: 'Lieu de naissance',
    createAccount: 'Créer un compte',
    alreadyHaveAccount: 'Vous avez déjà un compte ?',
    dontHaveAccount: "Vous n'avez pas de compte ?",
    
    // Common
    loading: 'Chargement...',
    error: 'Erreur',
    success: 'Succès',
    cancel: 'Annuler',
    save: 'Enregistrer',
    submit: 'Soumettre',
    back: 'Retour',
    next: 'Suivant',
    yes: 'Oui',
    no: 'Non',
    
    // Landing
    heroTitle: 'Décodez votre ADN de Leadership',
    heroSubtitle: "Une évaluation sans précédent où 22 cadres d'évaluation propriétaires sont synthétisés avec plus de 5000 ans de sciences de la sagesse indienne ancienne.",
    beginAssessment: "Commencer l'évaluation",
    
    // Aryan
    chatWithAryan: 'Discuter avec Aryan',
    askAryan: 'Posez à Aryan toutes vos questions sur votre rapport...',
    aryanGreeting: 'Bonjour ! Je suis Aryan, votre mentor personnel. Comment puis-je vous aider aujourd\'hui ?',
  },
  de: {
    // Navigation
    home: 'Startseite',
    dashboard: 'Dashboard',
    assessment: 'Bewertung',
    reports: 'Berichte',
    profile: 'Profil',
    logout: 'Abmelden',
    login: 'Anmelden',
    signIn: 'Anmelden',
    signUp: 'Registrieren',
    getStarted: 'Loslegen',
    
    // Assessment
    startAssessment: 'Bewertung starten',
    continueAssessment: 'Bewertung fortsetzen',
    submitAssessment: 'Bewertung einreichen',
    nextQuestion: 'Weiter',
    previousQuestion: 'Zurück',
    questionOf: 'Frage {current} von {total}',
    timeRemaining: 'Verbleibende Zeit',
    assessmentComplete: 'Bewertung abgeschlossen',
    
    // Reports
    viewReport: 'Bericht anzeigen',
    downloadReport: 'Bericht herunterladen',
    reportReady: 'Ihr Bericht ist fertig',
    reportPending: 'Bericht wird verarbeitet',
    reportWillBeEmailed: 'Ihr Bericht wird innerhalb von 24-48 Stunden per E-Mail gesendet',
    
    // Auth
    emailAddress: 'E-Mail-Adresse',
    password: 'Passwort',
    fullName: 'Vollständiger Name',
    mobileNumber: 'Handynummer',
    dateOfBirth: 'Geburtsdatum',
    timeOfBirth: 'Geburtszeit',
    placeOfBirth: 'Geburtsort',
    createAccount: 'Konto erstellen',
    alreadyHaveAccount: 'Haben Sie bereits ein Konto?',
    dontHaveAccount: 'Haben Sie noch kein Konto?',
    
    // Common
    loading: 'Wird geladen...',
    error: 'Fehler',
    success: 'Erfolg',
    cancel: 'Abbrechen',
    save: 'Speichern',
    submit: 'Einreichen',
    back: 'Zurück',
    next: 'Weiter',
    yes: 'Ja',
    no: 'Nein',
    
    // Landing
    heroTitle: 'Entschlüsseln Sie Ihre Führungs-DNA',
    heroSubtitle: 'Eine beispiellose Bewertung, bei der 22 proprietäre Bewertungsrahmen mit über 5000 Jahren alter indischer Weisheitswissenschaften synthetisiert werden.',
    beginAssessment: 'Bewertung beginnen',
    
    // Aryan
    chatWithAryan: 'Mit Aryan chatten',
    askAryan: 'Fragen Sie Aryan alles über Ihren Bericht...',
    aryanGreeting: 'Hallo! Ich bin Aryan, Ihr persönlicher Mentor. Wie kann ich Ihnen heute helfen?',
  },
  es: {
    // Navigation
    home: 'Inicio',
    dashboard: 'Panel de control',
    assessment: 'Evaluación',
    reports: 'Informes',
    profile: 'Perfil',
    logout: 'Cerrar sesión',
    login: 'Iniciar sesión',
    signIn: 'Iniciar sesión',
    signUp: 'Registrarse',
    getStarted: 'Comenzar',
    
    // Assessment
    startAssessment: 'Iniciar evaluación',
    continueAssessment: 'Continuar evaluación',
    submitAssessment: 'Enviar evaluación',
    nextQuestion: 'Siguiente',
    previousQuestion: 'Anterior',
    questionOf: 'Pregunta {current} de {total}',
    timeRemaining: 'Tiempo restante',
    assessmentComplete: 'Evaluación completada',
    
    // Reports
    viewReport: 'Ver informe',
    downloadReport: 'Descargar informe',
    reportReady: 'Su informe está listo',
    reportPending: 'Procesando informe',
    reportWillBeEmailed: 'Su informe será enviado por correo electrónico en 24-48 horas',
    
    // Auth
    emailAddress: 'Correo electrónico',
    password: 'Contraseña',
    fullName: 'Nombre completo',
    mobileNumber: 'Número de teléfono',
    dateOfBirth: 'Fecha de nacimiento',
    timeOfBirth: 'Hora de nacimiento',
    placeOfBirth: 'Lugar de nacimiento',
    createAccount: 'Crear cuenta',
    alreadyHaveAccount: '¿Ya tiene una cuenta?',
    dontHaveAccount: '¿No tiene una cuenta?',
    
    // Common
    loading: 'Cargando...',
    error: 'Error',
    success: 'Éxito',
    cancel: 'Cancelar',
    save: 'Guardar',
    submit: 'Enviar',
    back: 'Atrás',
    next: 'Siguiente',
    yes: 'Sí',
    no: 'No',
    
    // Landing
    heroTitle: 'Descifre su ADN de Liderazgo',
    heroSubtitle: 'Una evaluación sin precedentes donde 22 marcos de evaluación propietarios se sintetizan con más de 5000 años de ciencias de la sabiduría india antigua.',
    beginAssessment: 'Comenzar evaluación',
    
    // Aryan
    chatWithAryan: 'Chatear con Aryan',
    askAryan: 'Pregunte a Aryan cualquier cosa sobre su informe...',
    aryanGreeting: '¡Hola! Soy Aryan, tu mentor personal. ¿Cómo puedo ayudarte hoy?',
  },
  ru: {
    // Navigation
    home: 'Главная',
    dashboard: 'Панель управления',
    assessment: 'Оценка',
    reports: 'Отчёты',
    profile: 'Профиль',
    logout: 'Выйти',
    login: 'Войти',
    signIn: 'Войти',
    signUp: 'Регистрация',
    getStarted: 'Начать',
    
    // Assessment
    startAssessment: 'Начать оценку',
    continueAssessment: 'Продолжить оценку',
    submitAssessment: 'Отправить оценку',
    nextQuestion: 'Далее',
    previousQuestion: 'Назад',
    questionOf: 'Вопрос {current} из {total}',
    timeRemaining: 'Оставшееся время',
    assessmentComplete: 'Оценка завершена',
    
    // Reports
    viewReport: 'Посмотреть отчёт',
    downloadReport: 'Скачать отчёт',
    reportReady: 'Ваш отчёт готов',
    reportPending: 'Отчёт обрабатывается',
    reportWillBeEmailed: 'Ваш отчёт будет отправлен на email в течение 24-48 часов',
    
    // Auth
    emailAddress: 'Email адрес',
    password: 'Пароль',
    fullName: 'Полное имя',
    mobileNumber: 'Номер телефона',
    dateOfBirth: 'Дата рождения',
    timeOfBirth: 'Время рождения',
    placeOfBirth: 'Место рождения',
    createAccount: 'Создать аккаунт',
    alreadyHaveAccount: 'Уже есть аккаунт?',
    dontHaveAccount: 'Нет аккаунта?',
    
    // Common
    loading: 'Загрузка...',
    error: 'Ошибка',
    success: 'Успех',
    cancel: 'Отмена',
    save: 'Сохранить',
    submit: 'Отправить',
    back: 'Назад',
    next: 'Далее',
    yes: 'Да',
    no: 'Нет',
    
    // Landing
    heroTitle: 'Расшифруйте свою ДНК Лидерства',
    heroSubtitle: 'Беспрецедентная оценка, где 22 собственных системы оценки синтезированы с 5000+ летней мудростью древней Индии.',
    beginAssessment: 'Начать оценку',
    
    // Aryan
    chatWithAryan: 'Чат с Арьяном',
    askAryan: 'Спросите Арьяна о вашем отчёте...',
    aryanGreeting: 'Привет! Я Арьян, ваш личный наставник. Чем могу помочь сегодня?',
  },
  it: {
    // Navigation
    home: 'Home',
    dashboard: 'Dashboard',
    assessment: 'Valutazione',
    reports: 'Report',
    profile: 'Profilo',
    logout: 'Esci',
    login: 'Accedi',
    signIn: 'Accedi',
    signUp: 'Registrati',
    getStarted: 'Inizia',
    
    // Assessment
    startAssessment: 'Inizia valutazione',
    continueAssessment: 'Continua valutazione',
    submitAssessment: 'Invia valutazione',
    nextQuestion: 'Avanti',
    previousQuestion: 'Indietro',
    questionOf: 'Domanda {current} di {total}',
    timeRemaining: 'Tempo rimanente',
    assessmentComplete: 'Valutazione completata',
    
    // Reports
    viewReport: 'Visualizza report',
    downloadReport: 'Scarica report',
    reportReady: 'Il tuo report è pronto',
    reportPending: 'Report in elaborazione',
    reportWillBeEmailed: 'Il tuo report sarà inviato via email entro 24-48 ore',
    
    // Auth
    emailAddress: 'Indirizzo email',
    password: 'Password',
    fullName: 'Nome completo',
    mobileNumber: 'Numero di telefono',
    dateOfBirth: 'Data di nascita',
    timeOfBirth: 'Ora di nascita',
    placeOfBirth: 'Luogo di nascita',
    createAccount: 'Crea account',
    alreadyHaveAccount: 'Hai già un account?',
    dontHaveAccount: 'Non hai un account?',
    
    // Common
    loading: 'Caricamento...',
    error: 'Errore',
    success: 'Successo',
    cancel: 'Annulla',
    save: 'Salva',
    submit: 'Invia',
    back: 'Indietro',
    next: 'Avanti',
    yes: 'Sì',
    no: 'No',
    
    // Landing
    heroTitle: 'Decodifica il tuo DNA di Leadership',
    heroSubtitle: 'Una valutazione senza precedenti dove 22 framework di valutazione proprietari sono sintetizzati con oltre 5000 anni di scienze della saggezza indiana antica.',
    beginAssessment: 'Inizia la valutazione',
    
    // Aryan
    chatWithAryan: 'Chatta con Aryan',
    askAryan: 'Chiedi ad Aryan qualsiasi cosa sul tuo report...',
    aryanGreeting: 'Ciao! Sono Aryan, il tuo mentore personale. Come posso aiutarti oggi?',
  },
};

// Default labels for languages not yet fully translated
const DEFAULT_LABELS = UI_LABELS.en;

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState('en');
  const [isRTL, setIsRTL] = useState(false);

  // Load language from localStorage on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('preferredLanguage');
    if (savedLanguage) {
      setLanguageState(savedLanguage);
      const langConfig = SUPPORTED_LANGUAGES.find(l => l.code === savedLanguage);
      setIsRTL(langConfig?.rtl || false);
    }
  }, []);

  // Update document direction when language changes
  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language, isRTL]);

  const setLanguage = async (langCode) => {
    setLanguageState(langCode);
    localStorage.setItem('preferredLanguage', langCode);
    
    const langConfig = SUPPORTED_LANGUAGES.find(l => l.code === langCode);
    setIsRTL(langConfig?.rtl || false);

    // Update user profile if logged in
    const token = localStorage.getItem('token');
    if (token) {
      try {
        await fetch(`${API_URL}/api/user/language`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ language: langCode })
        });
      } catch (error) {
        console.error('Failed to update language preference:', error);
      }
    }
  };

  // Get label with fallback to English
  const t = (key, replacements = {}) => {
    const labels = UI_LABELS[language] || DEFAULT_LABELS;
    let text = labels[key] || DEFAULT_LABELS[key] || key;
    
    // Replace placeholders like {current} with actual values
    Object.keys(replacements).forEach(placeholder => {
      text = text.replace(`{${placeholder}}`, replacements[placeholder]);
    });
    
    return text;
  };

  // Get current language config
  const getCurrentLanguage = () => {
    return SUPPORTED_LANGUAGES.find(l => l.code === language) || SUPPORTED_LANGUAGES[0];
  };

  return (
    <LanguageContext.Provider value={{
      language,
      setLanguage,
      isRTL,
      t,
      getCurrentLanguage,
      supportedLanguages: SUPPORTED_LANGUAGES
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
